/**
 * How a finished run reads for health, bars and counts.
 *
 * Job `status` alone is not enough: before price-refresh status was fixed, a
 * run where every check failed was written `completed`, and those history rows
 * are still stored that way. So an outcome is derived from the result too.
 *
 * Keep in sync with `scraper/src/utils/runOutcome.ts` (`evaluateRunOutcome`);
 * `runOutcome.test.ts` and `src/__tests__/scraperRunOutcome.test.ts` pin both
 * to the same cases.
 */

/** Share of attempted items that may fail before a completed run reads as partial. */
export const PARTIAL_FAILURE_RATIO = 0.5;

export type RunOutcome = 'failed' | 'partial' | 'ok';

export interface RunOutcomeInput {
  status?: string;
  productsProcessed?: number | null;
  productsFailed?: number | null;
  /** Job type / Cloud Run mode: the ratio rules apply to price refreshes only. */
  type?: string | null;
  mode?: string | null;
}

/** Same predicate as `isPriceRefreshRun` in `scraper/src/utils/runOutcome.ts`. */
export function isPriceRefreshRun(run: Pick<RunOutcomeInput, 'type' | 'mode'> | null | undefined): boolean {
  return run?.type === 'price_refresh' || run?.mode === 'price_only';
}

/**
 * - `failed`: status failed, or (price refresh only) every attempted item failed
 *   (`productsProcessed > 0` and `productsFailed >= productsProcessed`).
 * - `partial`: more than half of the attempted items failed, not all.
 * - `ok`: everything else, including runs that attempted nothing.
 */
export function classifyRunOutcome(run: RunOutcomeInput | null | undefined): RunOutcome {
  if (!run) return 'ok';
  if (run.status === 'failed') return 'failed';
  // A catalog run that rejected rows is `completed` by design (zero-product rule
  // aside); only price refreshes are read by their failure ratio.
  if (!isPriceRefreshRun(run)) return 'ok';
  const attempted = Math.max(0, Number(run.productsProcessed) || 0);
  const failed = Math.min(attempted, Math.max(0, Number(run.productsFailed) || 0));
  if (attempted > 0 && failed === attempted) return 'failed';
  if (attempted > 0 && failed / attempted > PARTIAL_FAILURE_RATIO) return 'partial';
  return 'ok';
}
